/**
 * Dev Auth Service
 *
 * Servizio per autenticazione in modalità development/test.
 * Permette di ottenere token JWT di test dal backend
 * e gestire l'autenticazione senza Azure AD B2C.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Utenti di test disponibili
 */
export const TEST_USER_KEYS = [
  'admin',
  'rspp',
  'direzione',
  'direttore_rsa',
  'amministrativo',
  'fornitore'
];

/**
 * Ottiene tutti i token di test disponibili
 */
export async function getAllTestTokens() {
  const response = await fetch(`${API_BASE_URL}/api/auth/test-tokens`);
  if (!response.ok) {
    throw new Error('Test tokens endpoint not available');
  }
  return response.json();
}

/**
 * Ottiene un token JWT per uno specifico utente di test
 * @param {string} userKey - admin, rspp, direzione, etc.
 */
export async function getTestToken(userKey) {
  const response = await fetch(`${API_BASE_URL}/api/auth/test-token/${userKey}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get test token');
  }
  return response.json();
}

/**
 * Lista tutti gli utenti di test disponibili
 */
export async function getTestUsers() {
  const response = await fetch(`${API_BASE_URL}/api/auth/test-users`);
  if (!response.ok) {
    throw new Error('Test users endpoint not available');
  }
  return response.json();
}

/**
 * Salva il token nel localStorage
 * @param {string} token - JWT token
 * @param {object} user - User info
 */
export function saveDevToken(token, user) {
  localStorage.setItem('phc_dev_token', token);
  localStorage.setItem('phc_dev_user', JSON.stringify(user));
}

/**
 * Recupera il token dal localStorage
 */
export function getDevToken() {
  return localStorage.getItem('phc_dev_token');
}

/**
 * Recupera l'utente dal localStorage
 */
export function getDevUser() {
  const userJson = localStorage.getItem('phc_dev_user');
  return userJson ? JSON.parse(userJson) : null;
}

/**
 * Rimuove token e utente dal localStorage
 */
export function clearDevAuth() {
  localStorage.removeItem('phc_dev_token');
  localStorage.removeItem('phc_dev_user');
}

/**
 * Verifica se siamo in dev mode con auth abilitata
 */
export function isDevAuthEnabled() {
  return import.meta.env.DEV || import.meta.env.VITE_DEV_AUTH === 'true';
}

/**
 * Esegue login con un utente di test
 * @param {string} userKey - admin, rspp, direzione, etc.
 */
export async function devLogin(userKey) {
  const data = await getTestToken(userKey);
  saveDevToken(data.token, data.user);
  return data;
}

/**
 * Esegue logout in dev mode
 */
export function devLogout() {
  clearDevAuth();
}

/**
 * Controlla se l'utente è autenticato in dev mode
 */
export function isDevAuthenticated() {
  const token = getDevToken();
  if (!token) return false;

  // Check token expiration (basic check)
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    const payload = JSON.parse(atob(parts[1]));
    if (payload.exp && payload.exp < Date.now() / 1000) {
      clearDevAuth();
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export default {
  TEST_USER_KEYS,
  getAllTestTokens,
  getTestToken,
  getTestUsers,
  saveDevToken,
  getDevToken,
  getDevUser,
  clearDevAuth,
  isDevAuthEnabled,
  devLogin,
  devLogout,
  isDevAuthenticated
};
